import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import AuthImage from "./AuthImage";

/**
 * Full-screen image viewer.
 * - Dark overlay rgba(0,0,0,0.92)
 * - Image centered, max 90vw × 90vh, never cropped
 * - Close on ✕, click outside image, or Escape
 * - Left/right arrows when multiple images present
 * - 200ms fade-in
 */
export default function Lightbox({ images, startIndex = 0, onClose }) {
  const [i, setI] = useState(startIndex);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setI((cur) => (cur + 1) % images.length);
      if (e.key === "ArrowLeft") setI((cur) => (cur - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [images.length, onClose]);

  if (!images?.length) return null;
  const img = images[i];

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.92)", opacity: visible ? 1 : 0, transition: "opacity 200ms ease" }}
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 text-white hover:text-[#39ff14] p-2"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X size={28} />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setI((cur) => (cur - 1 + images.length) % images.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#00aaff] p-2"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setI((cur) => (cur + 1) % images.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#00aaff] p-2"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight size={36} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-tech text-xs text-[#94a3b8]" data-testid="lightbox-counter">
            {i + 1} / {images.length}
          </div>
        </>
      )}

      <AuthImage
        src={img.url || img}
        alt=""
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
    </div>
  );
}
